
import { defineComponent, PropType, computed, ref } from 'vue'
import config from '@root/config'
import {
    IntegrationInstallPayload,
    MarketplaceIntegration,
    MarketplaceIntegrationInstallStatus, OtherIntegrationInstallPayload,
} from '../../../types/marketplace'
import { useProject } from '../../../hooks/useProject'
import { declOfNum } from '../../../utils'
import { useMarketplaceStore } from '../../../store/stores/marketplace'
import IntegrationInstallModal from './IntegrationInstallModal/IntegrationInstallModal.vue'
import store from '@/store/index'
import MarketplacePaymentModal from '@/modules/marketplace/components/IntegrationInstallModal/MarketplacePaymentModal.vue'
import { useBillingStore } from '@/store/stores/billing'
import { SolvePaymentParams } from '@api/marketplace'
import { useConfirmStore } from '@/store/stores/confirm'
import { getIntegrationWithFormIds } from '@/helpers/marketplace/integrationPurchaseCount'
import { getPendingSlots } from '@/api/networkFixer'
import TrialOfferModal from '@/modules/marketplace/components/TrialOfferModal.vue'
import { findTrialOfferByIntegrationId, trialModulePath, trialPeriodLabel } from '@/modules/marketplace/trialOffers'
import { activateIntegrationTrial } from '@/api/marketplace'
import useRouter from '@/hooks/useRouter'
import {
    BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS,
    BEAUTY_AUTOPILOT_PLAN_LABELS,
    BeautyAutopilotPlan,
    getBeautyAutopilotPlan,
} from '@/helpers/beautyAutopilot'

const BEAUTY_AUTOPILOT_PLAN_DESCRIPTIONS: Record<BeautyAutopilotPlan, string> = {
    basis: 'Мотивационная схема, smart-дашборд и авто-задачи',
    optima: 'Всё из «Базиса» + библиотека задач и автозаполнение',
}

interface BeautyAutopilotPlanOption {
    plan: BeautyAutopilotPlan
    integrationId: string
    label: string
    price: string
    description: string
}
export default defineComponent({
    props: {
        integration: {
            type: Object as PropType<MarketplaceIntegration>,
            required: true,
        },
        disablePrimaryButton: {
            type: Boolean,
            default: false,
        },
        primaryButtonDisabledTooltip: {
            type: String,
            default: '',
        },
    },
    emits: ['open-request-sidebar', 'pending-payment-detected'],
    components: { IntegrationInstallModal, MarketplacePaymentModal, TrialOfferModal },
    setup(props, { emit }) {
        const confirmStore = useConfirmStore()
        const { project } = useProject()
        const { router } = useRouter()
        const billingStore = useBillingStore()
        const marketplaceStore = useMarketplaceStore()
        const installIntegrationModalOpened = ref(false)
        const trialOpen = ref(false)
        const activatingTrial = ref(false)
        const closeTrial = () => {
            if (activatingTrial.value) return
            trialOpen.value = false
        }
        const loading = ref(false)
        const integrationInstallPayload = ref<OtherIntegrationInstallPayload>({})

        const price = computed(() =>{
              if (!props.integration.price) {
                  return 'Бесплатно'
              } else {
                  if (props.integration.paymentType === 'subscription') {
                      return `От ${props.integration.price} ₽/мес.`
                  } else {
                      return `От ${props.integration.price} ₽.`
                  }
              }
        })
        const bottomText = computed(() => {
            const trial = props.integration.trialDays
            if (props.integration.status === 'freezed') return 'Приостановлено!'
            if (trial) return `Бесплатный период — ${trial} ${declOfNum(trial, ['день', 'дня', 'дней'])}`
        })
        const bottomTextClass = computed(() => {
            const trial = props.integration.trialDays
            if (props.integration.status === 'freezed') return 'freezed'
            if (trial) return 'trial'
            if (props.integration.type === 'activation') return 'activation'
        })


        const isSlotType = computed(
            () =>
                props.integration.type === 'maxSlot' ||
                props.integration.type === 'slot' ||
                props.integration.type === 'smsChannelSlot',
        )
        const moduleIds = computed(() => config.marketplaceModulesIds[config.buildType])
        const isDirectConnectIntegration = computed(() =>
            [
                moduleIds.value.dhChat,
                moduleIds.value.customCascades,
                moduleIds.value.zvonoBot,
                moduleIds.value.businessPulseReport,
                moduleIds.value.opz2,
                moduleIds.value.opz2KPISettings,
                moduleIds.value.zvonoBotSettings,
                moduleIds.value.hotWindows,
            ].includes(props.integration.id) || isSlotType.value,
        )
        // Beauty Autopilot: карточка одна, продаются строки планов «Базис» / «Оптима».
        const isBeautyAutopilotCard = computed(
            () => String(props.integration.id) === String(moduleIds.value.beautyAutopilot),
        )
        const beautyAutopilotPlanOptions = computed<BeautyAutopilotPlanOption[]>(() => {
            if (!isBeautyAutopilotCard.value) return []
            const integrations = marketplaceStore.integrations[String(project.value?.id)] ?? []
            return (Object.keys(BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS) as BeautyAutopilotPlan[]).flatMap((plan) => {
                const integration = integrations.find(
                    (el) => String(el.id) === BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS[plan],
                )
                if (!integration) return []
                return [
                    {
                        plan,
                        integrationId: String(integration.id),
                        label: BEAUTY_AUTOPILOT_PLAN_LABELS[plan],
                        price: integration.price ? `${integration.price.toLocaleString('ru-RU')} ₽/мес.` : '',
                        description: BEAUTY_AUTOPILOT_PLAN_DESCRIPTIONS[plan],
                    },
                ]
            })
        })
        // Покупка через биллинг — у проекта с биллингом и заведёнными в каталоге планами. Иначе заявка, как раньше.
        const isBeautyAutopilotPurchasable = computed(
            () =>
                isBeautyAutopilotCard.value &&
                Boolean(project.value?.billingEnabled) &&
                beautyAutopilotPlanOptions.value.length > 0,
        )
        const beautyAutopilotPlanDialog = ref(false)
        const installedButtonTextByIntegrationId: Record<string, string> = {
            [moduleIds.value.crmSettings]: 'Приобретено',
            [moduleIds.value.beautyAutopilot]: 'Подключено',
            [moduleIds.value.kpiSettings]: 'Приобретено',
            [moduleIds.value.businessPulseReport]: 'Подключено',
            [moduleIds.value.hotWindows]: 'Подключено',
            [moduleIds.value.salesTraining]: 'Приобретено',
            [moduleIds.value.diagnostics]: 'Приобретено',
            [moduleIds.value.dhChat]: 'Подключено',
            [moduleIds.value.customCascades]: 'Подключено',
            [moduleIds.value.channels]: 'Купить еще',
            [moduleIds.value.maxBusiness]: 'Купить еще',
            [moduleIds.value.sms]: 'Купить еще',
            [moduleIds.value.crmTO]: 'Приобретено',
            [moduleIds.value.zvonoBot]: 'Подключено',
            [moduleIds.value.opz2]: 'Подключено',
            [moduleIds.value.webinars]: 'Приобретено',
            [moduleIds.value.opz2KPISettings]: 'Приобретено',
            [moduleIds.value.zvonoBotSettings]: 'Приобретено',
        }
        const isInstalledActionAllowed = computed(() =>
            [moduleIds.value.channels, moduleIds.value.maxBusiness, moduleIds.value.sms].includes(props.integration.id)
        )
        const isInstalledLocked = computed(
            () =>
                props.integration.status === 'installed' &&
                Boolean(installedButtonTextByIntegrationId[props.integration.id]) &&
                !isInstalledActionAllowed.value
        )

        const connectButtonText = computed(() => {
            const status = props.integration.status as MarketplaceIntegrationInstallStatus | 'request'
            if (isBeautyAutopilotPurchasable.value && !['noProject', 'unauthorized'].includes(status)) {
                if (status !== 'installed') return ['Подключить']
                const plan = getBeautyAutopilotPlan(project.value)
                return [plan ? `Подключено · ${BEAUTY_AUTOPILOT_PLAN_LABELS[plan]}` : 'Подключено']
            }
            if (status === 'installed') {
                const mappedLabel = installedButtonTextByIntegrationId[props.integration.id]
                if (mappedLabel) {
                    return [mappedLabel]
                }
            }
            const connectLabel =
                props.integration.id === moduleIds.value.opz2KPISettings ||
                props.integration.id === moduleIds.value.zvonoBotSettings
                    ? 'Заказать настройку'
                    : 'Подключить'
            switch (props.integration.paymentType) {
                case 'subscription':
                    switch (status) {
                        case 'installed':
                            return [
                                isSlotType.value ? 'Купить еще' : 'Продлить',
                                'Отключить',
                            ]
                        case 'uninstalled':
                        case 'request':
                            return isDirectConnectIntegration.value ? [connectLabel] : ['Оставить заявку']
                        case 'freezed': return [connectLabel]

                        // case 'unauthorized': return ['ЗАРЕГИСТРИРОВАТЬСЯ'];
                        case 'unauthorized': return ['Оставить заявку'];
                        // case 'noProject': return ['COЗДАТЬ ПРОЕКТ'];
                        case 'noProject': return ['Оставить заявку'];
                        default:
                            return [''];
                    }
                case 'single':
                    switch (status) {
                        case 'installed' : return ['','Отключить'];
                        case 'uninstalled':
                        case 'request':
                            return isDirectConnectIntegration.value ? [connectLabel] : ['Оставить заявку']
                        // case 'unauthorized': return ['ЗАРЕГИСТРИРОВАТЬСЯ'];
                        case 'unauthorized': return ['Оставить заявку'];
                        // case 'noProject': return ['COЗДАТЬ ПРОЕКТ'];
                        case 'noProject': return ['Оставить заявку'];
                        default:
                            return [''];
                    }
                case 'ever':
                    switch (status) {
                        case 'installed' : return ['Выключить'];
                        case 'uninstalled':
                        case 'request':
                            return isDirectConnectIntegration.value ? [connectLabel] : ['Оставить заявку']
                        case 'freezed': return ['Включить'];
                        // case 'unauthorized': return ['ЗАРЕГИСТРИРОВАТЬСЯ'];
                        case 'unauthorized': return ['Оставить заявку'];
                        // case 'noProject': return ['COЗДАТЬ ПРОЕКТ'];
                        case 'noProject': return ['Оставить заявку'];
                        default:
                            return [''];
                    }
                default:
                    return [''];
            }
        });
        const isPrimaryButtonDisabled = computed(
            () => isInstalledLocked.value || props.disablePrimaryButton,
        )
        // «Подключить» — покупка. «Оставить заявку» — кнопки пробного периода нет.
        // Список интеграций с пробным периодом — marketplaceTrialOffers.
        const trialOffer = computed(() =>
            findTrialOfferByIntegrationId(props.integration.id, moduleIds.value),
        )
        const showTrialOffer = computed(
            () =>
                Boolean(trialOffer.value) &&
                connectButtonText.value[0] === 'Подключить' &&
                props.integration.isTrialUsed !== true,
        )
        const activateTrial = async () => {
            const projectId = project.value?.id
            const integrationId = props.integration.id
            if (activatingTrial.value || !projectId || !integrationId || !trialOffer.value) return
            activatingTrial.value = true
            const { error } = await activateIntegrationTrial({
                projectId: String(projectId),
                integrationId: String(integrationId),
            })
            activatingTrial.value = false
            if (error) {
                store.dispatch('callNotify', 'Не удалось активировать пробный период')
                return
            }
            trialOpen.value = false
            await marketplaceStore.fetchIntegrations(String(projectId))
            await store.dispatch('project/fetchProject', projectId)
            const path = trialModulePath(trialOffer.value.moduleKey, String(projectId))
            if (path) router.push(path).catch(() => {})
        }
        const trialButtonPeriod = computed(() =>
            trialOffer.value ? trialPeriodLabel(trialOffer.value.days) : '',
        )
        const isSecondaryButtonDisabled = computed(() => isInstalledLocked.value)
        const showPrimaryButtonDisabledTooltip = computed(
            () => isPrimaryButtonDisabled.value && Boolean(props.primaryButtonDisabledTooltip),
        )

        const connectSubText = computed(() => {
            const status = props.integration.status as MarketplaceIntegrationInstallStatus | 'request'
            const formatedDate = new Date(props.integration.activeUntil).toLocaleDateString()
            switch (props.integration.paymentType) {
                case 'subscription':
                    switch (status) {
                        case 'installed' : return `Следующий платеж ${formatedDate}`;
                        case 'uninstalled': return '';
                        case 'freezed': return `Истекает ${formatedDate}`;

                        case 'unauthorized': return '';
                        case 'noProject': return '';
                        case 'request': return '';
                        default:
                            return '';
                    }
                case 'single':
                    switch (status) {
                        case 'installed' : return 'Подключено';
                        case 'uninstalled': return '';

                        case 'unauthorized': return '';
                        case 'noProject': return '';
                        case 'request': return '';
                        default:
                            return '';
                    }
                case 'ever':
                    switch (status) {
                        case 'installed' : return '';
                        case 'uninstalled': return ;
                        case 'freezed': return '';

                        case 'unauthorized': return '';
                        case 'noProject': return '';
                        case 'request': return '';
                        default:
                            return '';
                    }
                default:
                    return '';
            }
        });

        const installButtonHandler = async () => {
            if (props.integration.status === 'noProject' || props.integration.status === 'unauthorized') {
                emit('open-request-sidebar')
                return
            }
            if (isBeautyAutopilotPurchasable.value) {
                beautyAutopilotPlanDialog.value = true
                return
            }
            if (getIntegrationWithFormIds().includes(String(props.integration.id))) {
                emit('open-request-sidebar')
                return
            }
            if (props.integration.id === moduleIds.value.dhChat) {
                return openDirectConnectModulePayment('Мультичат')
            }
            if (props.integration.id === moduleIds.value.customCascades) {
                return openDirectConnectModulePayment('Дополнительные каскады')
            }
            if (props.integration.id === moduleIds.value.zvonoBot) {
                return openDirectConnectModulePayment('Аудиорассылки DH')
            }
            if (props.integration.id === moduleIds.value.businessPulseReport) {
                return openDirectConnectModulePayment('Отчёт 2.0')
            }
            if (props.integration.id === moduleIds.value.hotWindows) {
                return openDirectConnectModulePayment('Горячие окна')
            }
            if (props.integration.id === moduleIds.value.opz2) {
                return openDirectConnectModulePayment('ОПЗ 2.0')
            }
            if (props.integration.id === moduleIds.value.opz2KPISettings) {
                return openDirectConnectModulePayment('Настройка KPI для ОПЗ 2.0')
            }
            if (props.integration.id === moduleIds.value.zvonoBotSettings) {
                return openDirectConnectModulePayment('Настройка Аудиорассылок DH')
            }
            if (props.integration.type === 'module') {
                return installModule()
            }
            if (props.integration.type === 'activation') {
                installIntegrationModalOpened.value = true
                return
            }
            if (props.integration.type === 'setting') {
                await installActivation()
                return
            }
            if (props.integration.type === 'maxSlot') {
                return openChannelPaymentSidebar({
                    name: 'MAX Business',
                    kind: 'bundle',
                })
            }
            // SMS приходит с type=slot и id=smsChannelSlot — проверяем id до общего slot
            if (
                props.integration.id === moduleIds.value.sms ||
                props.integration.type === 'smsChannelSlot'
            ) {
                return openChannelPaymentSidebar({
                    name: 'SMS DH',
                    kind: 'bundle',
                })
            }
            if (props.integration.type === 'slot') {
                return openChannelPaymentSidebar({
                    name: 'WA/TG/MAX/SMS',
                    kind: 'bundle',
                })
            }
        }

        const ensureBillingEnabled = () => {
            if (!project.value?.id || !project.value?.billingEnabled) {
                store.dispatch('callNotify', 'Оплата недоступна для этого проекта')
                return false
            }

            return true
        }

        const hasPendingPaymentForCurrentIntegration = async (integrationId: string = String(props.integration.id)) => {
            const projectId = project.value?.id
            if (!projectId) return false
            const { data, error } = await getPendingSlots({ projectId })
            if (error || !Array.isArray(data)) return false
            const currentIntegrationId = String(integrationId)
            return data.some((payload: any) => {
                const payloadIntegrationId = String(
                    payload?.integration_id ||
                        payload?.price?.lineItems?.[0]?.integrationId ||
                        '',
                )
                return payloadIntegrationId === currentIntegrationId
            })
        }

        const openDirectConnectModulePayment = async (name: string) => {
            if (!ensureBillingEnabled()) return
            if (await hasPendingPaymentForCurrentIntegration()) {
                emit('pending-payment-detected')
                store.dispatch('callNotify', 'Оплата в обработке')
                return
            }
            openChannelPaymentSidebar({
                name,
                kind: 'bundle',
            })
        }

        const openBeautyAutopilotPayment = async (option: BeautyAutopilotPlanOption) => {
            beautyAutopilotPlanDialog.value = false
            if (!ensureBillingEnabled()) return
            if (await hasPendingPaymentForCurrentIntegration(option.integrationId)) {
                emit('pending-payment-detected')
                store.dispatch('callNotify', 'Оплата в обработке')
                return
            }
            billingStore.setAddChannelContext({
                name: `Beauty Autopilot — ${option.label}`,
                icon: null,
                kind: 'bundle',
                itemKind: 'module',
                integrationId: option.integrationId,
            })
            billingStore.setPaymentModalOpened(true, false)
        }

        const openChannelPaymentSidebar = (context: { name: string; kind?: string }) => {
            if (!ensureBillingEnabled()) return
            billingStore.setAddChannelContext({
                name: context.name,
                icon: null,
                kind: context.kind || 'bundle',
                integrationId: props.integration.id,
            })
            billingStore.setPaymentModalOpened(true, false)
        }
        const freezeButtonHandler = async () => {
            if (props.integration.type === 'activation' || props.integration.type === 'setting') {
                await marketplaceStore.uninstallIntegration({
                    integrationId: props.integration.id,
                    projectId: project.value.id,
                })
                loading.value = false
                return
            }
            switch (props.integration.paymentType) {
                case 'subscription':
                    if (props.integration.status === 'installed') {
                        return uninstallModule()
                    }
                case 'single':

                case 'ever':

                default:
                    return [''];
            }
        }
        const solveIntegration = async (installPayload?: IntegrationInstallPayload) => {
            if (!ensureBillingEnabled()) return

            if (installPayload) {
                integrationInstallPayload.value = installPayload
            } else {
                integrationInstallPayload.value = {}
            }
            integrationInstallPayload.value.projectId = project.value.id
            integrationInstallPayload.value.marketplaceIntegrationId = props.integration.id
            integrationInstallPayload.value.subscriptionPeriod = 1
            billingStore.setAddChannelContext(null)
            const renewal = props.integration.status === 'installed'
            billingStore.setPaymentModalOpened(true, renewal)
        }

        const installIntegration = async (installPayload?: IntegrationInstallPayload) => {
            loading.value = true
            if (props.integration.status === 'uninstalled') {
                await marketplaceStore.installIntegration({
                    integrationId: props.integration.id,
                    projectId: project.value.id,

                    installPayload,
                })
                loading.value = false
                return
            }
            if (props.integration.status === 'installed') {
                await marketplaceStore.uninstallIntegration({
                    integrationId: props.integration.id,
                    projectId: project.value.id,
                })
                loading.value = false
                return
            }
            loading.value = false
        }

        const installModule = async () => {
            solveIntegration()
        }

        const uninstallModule = () => {
            confirmStore.confirm({
                text: `Вы уверены что хотите отключить подписку?`,
                onConfirm: async () => {
                    loading.value = true
                    if (props.integration.paymentType === 'subscription') {
                        await billingStore.disableSubscription({
                            projectId:project.value.id,
                            subscriptionId: props.integration.subscriptionId
                        })
                    } else {
                        await billingStore.disableIntegration({
                            projectId:project.value.id,
                            installId: props.integration.installId
                        })
                    }


                    window.location.reload()
                },
            })
        }

        const installActivation = async (installPayload?: IntegrationInstallPayload) => {
            loading.value = true
            if (props.integration.status === 'uninstalled') {
                await marketplaceStore.installIntegration({
                    integrationId: props.integration.id,
                    projectId: project.value.id,
                    installPayload,
                })
                loading.value = false
                return
            }
            if (props.integration.status === 'installed') {
                await marketplaceStore.uninstallIntegration({
                    integrationId: props.integration.id,
                    projectId: project.value.id,
                })
                loading.value = false
                return
            }
            loading.value = false
        }

        return {
            price,
            bottomText,
            bottomTextClass,
            connectButtonText,
            loading,
            installIntegration,
            installButtonHandler,
            freezeButtonHandler,
            installIntegrationModalOpened,
            solveIntegration,
            integrationInstallPayload,
            connectSubText,
            isPrimaryButtonDisabled,
            isSecondaryButtonDisabled,
            showPrimaryButtonDisabledTooltip,
            primaryButtonDisabledTooltip: computed(() => props.primaryButtonDisabledTooltip),
            installActivation,
            isSlotType,
            isDirectConnectIntegration,
            moduleIds,
            showTrialOffer,
            trialOffer,
            trialButtonPeriod,
            trialOpen,
            activatingTrial,
            activateTrial,
            closeTrial,
            isBeautyAutopilotCard,
            isBeautyAutopilotPurchasable,
            beautyAutopilotPlanOptions,
            beautyAutopilotPlanDialog,
            openBeautyAutopilotPayment,
        }
    },
})
